import Masonry from 'masonry-layout';
import Offcanvas from 'smg/offcanvas';
import { G } from 'smg/querystring';

if (document.body.id === 'gallery') {
    main();
}

function main() {
    const masonry = new Masonry('#gallery-images', {
        itemSelector: '.gallery-image-parent:not(.d-none)',
    });

    const $filterSections = document.querySelectorAll<HTMLElement>('.filter-section');
    const $filterCheckboxes = document.querySelectorAll<HTMLInputElement>('.filter-checkbox');

    const $imgParents = document.querySelectorAll<HTMLAnchorElement>('.gallery-image-parent');
    const imageDetails = new Map<HTMLElement, Map<string, string>>();
    for (const $imageParent of Array.from($imgParents)) {
        const $img = $imageParent.querySelector('img') as HTMLImageElement;
        const detailsJSON = $img.getAttribute('data-details') || '';
        const details = JSON.parse(detailsJSON) as Record<string, string>;

        const detailsMap = new Map<string, string>();
        for (const [key, value] of Object.entries(details)) {
            detailsMap.set(key, value);
        }

        imageDetails.set($imageParent, detailsMap);
    }

    const compareImageToFilters = (image: HTMLElement, filters: Map<string, Set<string>>) => {
        const details = imageDetails.get(image);
        if (!details) {
            return false;
        }

        let show = true;
        for (const [filterName, filterValue] of filters) {
            if (!details.has(filterName) || !filterValue.has(details.get(filterName) as string)) {
                show = false;
                break;
            }
        }
        return show;
    };

    const getFilteredImages = (filters: Map<string, Set<string>>) => {
        const filteredImages = new Set<HTMLElement>();
        imageDetails.forEach((details, $image) => {
            if (compareImageToFilters($image, filters)) {
                filteredImages.add($image);
            }
        });
        return filteredImages;
    };

    const checkedFilters: () => Map<string, Set<string>> = () => {
        const filters = new Map<string, Set<string>>();
        $filterCheckboxes.forEach($checkbox => {
            if ($checkbox.checked) {
                const filter = String($checkbox.getAttribute('data-filter') || '');
                const value = $checkbox.value;

                if (!filters.get(filter)) {
                    filters.set(filter, new Set([value]));
                } else {
                    filters.get(filter)?.add(value);
                }
            }
        });

        return filters;
    };

    const toggleImages = () => {
        const filters = checkedFilters();

        $imgParents.forEach($ => $.classList.remove('d-none'));

        if (filters.size === 0) {
            $filterCheckboxes.forEach($checkbox => {
                $checkbox.disabled = false;
                $checkbox.parentElement?.classList.remove('d-none');
            });

            $filterSections.forEach($section => {
                $section.classList.remove('d-none');
            });

            setTimeout(() => {
                masonry.reloadItems?.();
                masonry.layout?.();
            });
            return;
        }

        const filteredImages = getFilteredImages(filters);
        imageDetails.forEach((details, $image) => {
            if (!filteredImages.has($image)) {
                $image.classList.add('d-none');
            }
        });

        $filterCheckboxes.forEach($checkbox => {
            $checkbox.disabled = false;
            $checkbox.parentElement?.classList.remove('d-none');

            if ($checkbox.checked) {
                return;
            }

            const filter = $checkbox.getAttribute('data-filter') || '';
            const value = $checkbox.value;

            const filtersIfChecked = new Map<string, Set<string>>();
            for (const [filterName, filterValue] of filters) {
                filtersIfChecked.set(filterName, new Set(filterValue));
            }
            if (!filtersIfChecked.has(filter)) {
                filtersIfChecked.set(filter, new Set());
            }
            filtersIfChecked.get(filter)?.add(value);
            const filteredImagesIfChecked = getFilteredImages(filtersIfChecked);

            let filterResultsSame = true;
            if (filteredImagesIfChecked.size !== filteredImages.size) {
                filterResultsSame = false;
            }
            for (const $image of filteredImages) {
                if (!filteredImagesIfChecked.has($image)) {
                    filterResultsSame = false;
                    break;
                }
            }

            if (filteredImagesIfChecked.size === 0 || filterResultsSame) {
                $checkbox.disabled = true;
                $checkbox.parentElement?.classList.add('d-none');
            }
        });

        $filterSections.forEach($section => {
            $section.classList.remove('d-none');
            const $checkboxes = $section.querySelectorAll<HTMLInputElement>('.filter-checkbox');
            if (Array.from($checkboxes).every($checkbox => $checkbox.disabled)) {
                $section.classList.add('d-none');
            }
        });

        setTimeout(() => {
            masonry.reloadItems?.();
            masonry.layout?.();
        });
    };

    let filtersQueryString = G().Get('filters');
    if (typeof filtersQueryString === 'undefined') {
        if (window.location.host.includes('challengecoins')) {
            filtersQueryString = 'lineItem=Challenge Coins';
        } else if (window.location.host.includes('pins')) {
            filtersQueryString = 'lineItem=Lapel Pins';
        } else if (window.location.host.includes('patches')) {
            filtersQueryString = 'lineItem=Patches';
        } else if (window.location.host.includes('lanyards')) {
            filtersQueryString = 'lineItem=Lanyards';
        }
    }
    if (filtersQueryString) {
        const filters: Record<string, string[]> = filtersQueryString.split('|').reduce((acc: Record<string, string[]>, filter) => {
            const [key, value] = filter.split('=');
            acc[key] = value.split('_');
            return acc;
        }, {});

        Object.entries(filters).forEach(([filter, values]) => {
            values.forEach(value => {
                const $checkbox = document.querySelector<HTMLInputElement>(`input[data-filter="${CSS.escape(filter)}"][value="${CSS.escape(value)}"]`);
                if ($checkbox) {
                    $checkbox.checked = true;
                }
            });
        });
    }

    const setQueryString = () => {
        const filters = checkedFilters();
        // values joined by `_`, filters joined by `|`
        G().Set('filters', Array.from(filters).map(([filter, values]) => `${filter}=${Array.from(values).join('_')}`).join('|'));
    };

    $filterCheckboxes.forEach($checkbox => {
        $checkbox.addEventListener('change', () => {
            setQueryString();
            toggleImages();
        });
    });

    const $filtersParent = document.querySelector('#gallery-filters-parent') as HTMLElement;
    const $filters = document.querySelector('#gallery-filters') as HTMLElement;

    const $showFilters = document.querySelector<HTMLButtonElement>('#gallery-show-filters');
    $showFilters?.addEventListener('click', () => {
        new Offcanvas({
            title: 'Filters',
            body: $filters,
            cancelText: 'Close',
            onClose() {
                $filtersParent.appendChild($filters);
            },
        });
    });

    const $spinner = document.querySelector('#gallery-spinner') as HTMLElement;
    $spinner.remove();

    const $galleryContainer = document.querySelector('#gallery-container') as HTMLElement;
    $galleryContainer.classList.remove('d-none');

    toggleImages();
}
